import { useTranslation } from 'react-i18next';

/**
 * One string for every place the customer meets cold-stored delivery files
 * (#2678): the download action in the Requests table, the download control on
 * the request itself, and the placeholder where the archived file would have
 * played.
 *
 * All three used to say something different, and two of them told the customer
 * to unarchive the files themselves — which is no longer something they can do,
 * so the self-service Unarchive button is gone and every one of those places
 * has to point at the same next step: support.
 *
 * Lives in the `actions` namespace because that is the one the table's action
 * copy already uses, and it is loaded globally (LocalizationClient.types).
 */
const requestFilesArchivedKey = 'download_files_archived';

/**
 * Two short sentences, deliberately. The tightest of the three places this
 * renders is a tooltip in the Requests table's Action column, where a longer
 * sentence wrapped to three lines and covered the row above it. "Download
 * unavailable" was the first thing to cut — the control it hangs off is
 * visibly dead already, so the copy only has to carry the why and the what-next.
 */
const requestFilesArchivedDefault = 'Files archived after 3 months. Contact support to unarchive.';

const useRequestFilesArchivedMessage = (): string => {
  const { t } = useTranslation('actions');

  return t(requestFilesArchivedKey, { defaultValue: requestFilesArchivedDefault });
};

/**
 * The other half of the same story, for the window where a restore is already
 * running: still not downloadable, but contacting support again would not move
 * it along, so it says so instead.
 */
const requestFilesRestoringKey = 'download_files_restoring';

const requestFilesRestoringDefault = 'Unarchiving — this can take up to 24 hours.';

const useRequestFilesRestoringMessage = (): string => {
  const { t } = useTranslation('actions');

  return t(requestFilesRestoringKey, { defaultValue: requestFilesRestoringDefault });
};

export {
  useRequestFilesArchivedMessage,
  useRequestFilesRestoringMessage,
  requestFilesArchivedKey,
  requestFilesArchivedDefault,
  requestFilesRestoringKey,
  requestFilesRestoringDefault,
};
